// React Imports
import { useRef } from 'react';

// MUI Imports
import { styled } from '@mui/material/styles';

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar';

// Type Imports
import type { ChildrenType } from '../../../@core/types';

import NavCollapseIcons from '../../../@menu/components/vertical-menu/NavCollapseIcons';
// Component Imports
import NavHeader from '../../../@menu/components/vertical-menu/NavHeader';
import Logo from '../shared/Logo';

// Hook Imports
import useHorizontalNav from '../../../@menu/hooks/useHorizontalNav';

import { DsIcon } from '../../../../components/icons/DsIcon';
// Util Imports
import { mapHorizontalToVerticalMenu } from '../../../@menu/utils/menuUtils';

const StyledBoxForShadow = styled('div')(({ theme }) => ({
  top: 60,
  left: -8,
  zIndex: 2,
  opacity: 0,
  position: 'absolute',
  pointerEvents: 'none',
  width: 'calc(100% + 15px)',
  height: theme.mixins.toolbar.minHeight,
  transition: 'opacity .15s ease-in-out',
  background: `linear-gradient(var(--mui-palette-background-default) ${
    theme.direction === 'rtl' ? '95%' : '5%'
  }, rgb(var(--mui-palette-background-defaultChannel) / 0.85) 30%, rgb(var(--mui-palette-background-defaultChannel) / 0.5) 65%, rgb(var(--mui-palette-background-defaultChannel) / 0.3) 75%, transparent)`,
  '&.scrolled': {
    opacity: 1,
  },
}));

export const VerticalNavContent = ({ children }: ChildrenType) => {
  // Hooks
  const { isBreakpointReached } = useHorizontalNav();

  // Refs
  const shadowRef = useRef(null);

  // Vars
  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar;

  const scrollMenu = (container: any, isPerfectScrollbar: boolean) => {
    const containerVar = isBreakpointReached || !isPerfectScrollbar ? container.target : container;

    if (shadowRef && containerVar.scrollTop > 0) {
      if (!(shadowRef.current as any).classList.contains('scrolled')) {
        (shadowRef.current as any).classList.add('scrolled');
      }
    } else {
      (shadowRef.current as any).classList.remove('scrolled');
    }
  };

  return (
    <>
      <NavHeader>
        <a href="/">
          <Logo />
        </a>
        <NavCollapseIcons
          lockedIcon={<DsIcon icon="CHEVRON_LEFT" size="md" />}
          unlockedIcon={<DsIcon icon="CHEVRON_RIGHT" size="lg" />}
          closeIcon={<DsIcon icon="CLOSE" size="md" />}
          className="text-textSecondary"
        />
      </NavHeader>
      <StyledBoxForShadow ref={shadowRef} />
      <ScrollWrapper
        {...(isBreakpointReached
          ? {
              className: 'bs-full overflow-y-auto overflow-x-hidden',
              onScroll: (container) => scrollMenu(container, false),
            }
          : {
              options: { wheelPropagation: false, suppressScrollX: true },
              onScrollY: (container) => scrollMenu(container, true),
            })}
      >
        {mapHorizontalToVerticalMenu(children)}
      </ScrollWrapper>
    </>
  );
};
